import { useEffect, useState } from 'react';
import { useImageCache } from '@/providers/image-cache-provider';

interface CachedImageProps {
  src: string | undefined;
  alt: string;
  className?: string;
  fallback?: React.ReactNode;
  loadingComponent?: React.ReactNode;
}

export function CachedImage({ src, alt, className, fallback, loadingComponent }: CachedImageProps) {
  const { getCachedImage, cacheImage } = useImageCache();
  const [imageState, setImageState] = useState<{
    src: string | null;
    loading: boolean;
    error: boolean;
  }>({ src: null, loading: false, error: false });

  useEffect(() => {
    if (!src || src.trim() === '') {
      setImageState({ src: null, loading: false, error: true });
      return;
    }

    // Check cache first
    const cached = getCachedImage(src);
    if (cached) {
      if (cached.objectUrl && !cached.loading && !cached.error) {
        setImageState({ src: cached.objectUrl, loading: false, error: false });
        return;
      }
      if (cached.error) {
        setImageState({ src: null, loading: false, error: true });
        return;
      }
      if (cached.loading) {
        setImageState({ src: null, loading: true, error: false });
        return;
      }
    }

    // Not in cache or invalid, fetch it
    setImageState({ src: null, loading: true, error: false });

    cacheImage(src)
      .then((objectUrl) => {
        setImageState({ src: objectUrl, loading: false, error: false });
      })
      .catch(() => {
        setImageState({ src: null, loading: false, error: true });
      });
  }, [src, getCachedImage, cacheImage]);

  // Show loading state
  if (imageState.loading) {
    if (loadingComponent) {
      return <>{loadingComponent}</>;
    }
    return (
      <div
        className={`bg-gray-200 animate-pulse rounded-full ${className || ''}`}
        role="img"
        aria-label={`Loading ${alt}`}
      />
    );
  }

  // Show error state or fallback
  if (imageState.error || !imageState.src) {
    if (fallback) {
      return <>{fallback}</>;
    }
    return (
      <div
        className={`bg-gray-300 rounded-full flex items-center justify-center ${className || ''}`}
        role="img"
        aria-label={alt}
      >
        <span className="text-gray-600 text-xs">?</span>
      </div>
    );
  }

  // Show the image
  return (
    <img
      src={imageState.src}
      alt={alt}
      className={className}
      onError={() => {
        setImageState({ src: null, loading: false, error: true });
      }}
    />
  );
}
